import { useMemo, useState } from 'react';
import type { CurrentUser } from '@wundo/client/auth.ts';
import { addDays, daysFrom, startOfWeek } from '@/lib/week.ts';
import { useToday } from './useToday.ts';

/* Until the preferences load. Both are personal settings on the agenda tab. */
const DEFAULT_WEEK_STARTS_ON = 'monday';

export interface AgendaWeek {
  weekStart: Date;
  /** Five weekdays, or seven with the weekend switched on. */
  visibleDays: number;
  days: Date[];
  showThisWeek: () => void;
  showPreviousWeek: () => void;
  showNextWeek: () => void;
}

/**
 * The week on screen, kept as an offset from this week rather than a date: the
 * week start is a preference that arrives after the first render, and a date
 * fixed at mount would keep the Monday after the user switched to Sunday.
 */
export function useAgendaWeek(user: CurrentUser | undefined): AgendaWeek {
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStartsOn = user?.agenda_week_starts_on ?? DEFAULT_WEEK_STARTS_ON;
  const visibleDays = user?.agenda_show_weekend ? 7 : 5;

  const now = useToday();
  const weekStart = useMemo(
    () => startOfWeek(addDays(now, weekOffset * 7), weekStartsOn),
    [now, weekOffset, weekStartsOn],
  );
  const days = useMemo(() => daysFrom(weekStart, visibleDays), [weekStart, visibleDays]);

  return {
    weekStart,
    visibleDays,
    days,
    showThisWeek: () => setWeekOffset(0),
    showPreviousWeek: () => setWeekOffset((weeks) => weeks - 1),
    showNextWeek: () => setWeekOffset((weeks) => weeks + 1),
  };
}
